import { renderProfile } from "./chart.js";
import { fetchGpxText, readFileAsText } from "./gpx.js";
import { buildElevationProfile } from "./profile.js";
import { MAX_GPX_BYTES, debounce, formatDistance, formatElevation } from "./utils.js";

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".bhgpxe").forEach(bootElevationProfile);
});

function bootElevationProfile(root) {
  const input = root.querySelector(".bhgpxe__gpxInput");
  const dropzone = root.querySelector(".bhgpxe__dropzone");
  const status = root.querySelector(".bhgpxe__status");
  const stats = root.querySelector(".bhgpxe__stats");
  const kmStats = root.querySelector(".bhgpxe__kmStats");
  const chartWrap = root.querySelector(".bhgpxe__chartWrap");
  const chart = root.querySelector(".bhgpxe__chart");
  let currentProfile = null;

  input.addEventListener("change", (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      loadFromFile(file);
    }
    event.target.value = "";
  });

  ["dragenter", "dragover"].forEach((eventName) => {
    dropzone.addEventListener(eventName, (event) => {
      event.preventDefault();
      event.stopPropagation();
      dropzone.classList.add("dragover");
    });
  });

  ["dragleave", "drop"].forEach((eventName) => {
    dropzone.addEventListener(eventName, (event) => {
      event.preventDefault();
      event.stopPropagation();
      dropzone.classList.remove("dragover");
    });
  });

  dropzone.addEventListener("drop", (event) => {
    const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
    if (file) {
      loadFromFile(file);
    }
  });

  window.addEventListener("resize", debounce(() => {
    if (currentProfile) {
      renderProfile(currentProfile, root, chartWrap, chart);
    }
  }, 150));

  if (root.dataset.gpxUrl) {
    loadFromUrl(root.dataset.gpxUrl);
  }

  async function loadFromFile(file) {
    if (!file.name.toLowerCase().endsWith(".gpx")) {
      setStatus("Please upload a .gpx file.", true);
      return;
    }

    if (file.size > MAX_GPX_BYTES) {
      setStatus("The GPX file is too large.", true);
      return;
    }

    await loadProfile(() => readFileAsText(file), true);
  }

  async function loadFromUrl(urlValue) {
    await loadProfile(() => fetchGpxText(urlValue));
  }

  async function loadProfile(loadText, trackUpload = false) {
    setStatus("Reading GPX elevation data...", false);
    chart.innerHTML = "";
    chartWrap.classList.remove("has-chart");
    stats.hidden = true;
    kmStats.hidden = true;
    kmStats.innerHTML = "";

    try {
      const gpxText = await loadText();
      currentProfile = buildElevationProfile(gpxText);
      updateStats(currentProfile);
      renderKmStats(currentProfile);
      renderProfile(currentProfile, root, chartWrap, chart);
      stats.hidden = false;
      kmStats.hidden = !currentProfile.kmSplits.length;
      setStatus(`Distance: ${formatDistance(currentProfile.totalDistance)}`, false);
      if (trackUpload) {
        trackGpxUpload();
      }
    } catch (error) {
      currentProfile = null;
      console.error(error);
      setStatus(error && error.message ? error.message : "Unable to render the GPX elevation profile.", true);
    }
  }

  function setStatus(message, isError) {
    status.textContent = message;
    status.classList.toggle("bhgpxe__status--error", Boolean(isError));
  }

  function trackGpxUpload() {
    if (typeof window.gtag !== "function") {
      return;
    }

    window.gtag("event", "Upload GPX", {
      event_category: "GPX Elevation",
      event_label: "bh_gpx_elevation",
    });
  }

  function updateStats(profile) {
    stats.querySelector('[data-stat="ascent"]').textContent = formatElevation(profile.totalAscent);
    stats.querySelector('[data-stat="descent"]').textContent = formatElevation(profile.totalDescent);
    stats.querySelector('[data-stat="min"]').textContent = formatElevation(profile.minElevation);
    stats.querySelector('[data-stat="max"]').textContent = formatElevation(profile.maxElevation);
  }

  function renderKmStats(profile) {
    kmStats.innerHTML = profile.kmSplits.map((split) => `
      <div class="bhgpxe__kmStat">
        <span>${formatSplitRange(split)}</span>
        ${formatSplitChanges(split)}
      </div>
    `).join("");
  }

  function formatSplitChanges(split) {
    return `
      <strong aria-label="Ascent: ${formatElevation(split.totalAscent)}">
        ${formatElevation(split.totalAscent)}
        <span aria-hidden="true">↗</span>
      </strong>
      <em aria-label="Descent: ${formatElevation(split.totalDescent)}">
        ${formatElevation(split.totalDescent)}
        <span aria-hidden="true">↘</span>
      </em>
    `;
  }

  function formatSplitRange(split) {
    const start = formatKmValue(split.startDistance);
    const end = formatKmValue(split.endDistance);
    return `${start}-${end} km`;
  }

  function formatKmValue(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }
}
